import { IEvidence, ProgramSkill } from "@interfaces/Interfaces";
import React from "react";

type SkillsRAGDisplayProps = {
  skills: ProgramSkill[];
  evidence: IEvidence[];
  showHeaderOnly?: boolean;
};

const SkillsRAGDisplay: React.FC<SkillsRAGDisplayProps> = (props) => {
  const skillsCount = props.skills.length;

  const nonRejectedEvidence = props.evidence.filter((e) => e.evidenceJSON?.programInfo?.approvalState !== "Rejected");
  const skillsWithSummative = new Set(
    nonRejectedEvidence
      .filter((e) => e.evidenceJSON?.programInfo?.assessmentType === "Summative")
      .map((e) => e.evidenceJSON?.programInfo?.skillID)
      .filter(Boolean)
  );
  const greenSkillsCount = props.skills.filter((s) => skillsWithSummative.has(s.ID)).length;
  const amberSkillsCount = props.skills.filter((s) => {
    const hasFormative = nonRejectedEvidence.some(
      (e) =>
        e.evidenceJSON?.programInfo?.skillID === s.ID && e.evidenceJSON?.programInfo?.assessmentType === "Formative"
    );
    return hasFormative && !skillsWithSummative.has(s.ID);
  }).length;
  const redSkillsCount = skillsCount - greenSkillsCount - amberSkillsCount;

  const greenSkillsPercentage = Math.floor((greenSkillsCount / skillsCount) * 100);
  const amberSkillsPercentage = Math.floor((amberSkillsCount / skillsCount) * 100);
  const redSkillsPercentage = Math.floor((redSkillsCount / skillsCount) * 100);

  return (
    <div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:gap-0">
        <div className="flex grow flex-col">
          <div className="flex flex-row items-end">
            <div className="text-acc-34px font-acc-700 leading-none tracking-[-0.5] text-green-light-text">
              {greenSkillsCount}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-60">
              &nbsp;{`/ ${skillsCount} Competencies`}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-90">
              &nbsp;{`(${greenSkillsPercentage}%)`}
            </div>
          </div>
          <div className="mt-1.5 text-acc-11px font-acc-600 leading-none tracking-default text-grey-60">
            {"Summatively evidenced".toUpperCase()}
          </div>
        </div>
        <div className="flex grow flex-col">
          <div className="flex flex-row items-end">
            <div className="text-acc-34px font-acc-700 leading-none tracking-[-0.5] text-orange-50">
              {amberSkillsCount}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-60">
              &nbsp;{`/ ${skillsCount} Competencies`}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-90">
              &nbsp;{`(${amberSkillsPercentage}%)`}
            </div>
          </div>
          <div className="mt-1.5 text-acc-11px font-acc-600 leading-none tracking-default text-grey-60">
            {"Formatively evidenced".toUpperCase()}
          </div>
        </div>
        <div className="flex grow flex-col">
          <div className="flex flex-row items-end">
            <div className="text-acc-34px font-acc-700 leading-none tracking-[-0.5] text-grey-60">{redSkillsCount}</div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-60">
              &nbsp;{`/ ${skillsCount} Competencies`}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-90">
              &nbsp;{`(${redSkillsPercentage}%)`}
            </div>
          </div>
          <div className="mt-1.5 text-acc-11px font-acc-600 leading-none tracking-default text-grey-60">
            {"Not Evidenced".toUpperCase()}
          </div>
        </div>
      </div>
      {!props.showHeaderOnly && (
        <>
          <div className="mt-2 flex flex-col">
            <div className="flex h-14 flex-row items-center justify-between">
              <div>{"Summative"}</div>
              <div className="flex size-7 items-center justify-center rounded-full bg-green-light-text">
                <div className="text-12px font-semibold text-white">{`${greenSkillsCount}`}</div>
              </div>
            </div>
            <div className="h-[1px] w-full bg-grey-30" />
          </div>
          <div className="flex flex-col">
            <div className="flex h-14 flex-row items-center justify-between">
              <div>{"Formative only"}</div>
              <div className="flex size-7 items-center justify-center rounded-full bg-orange-400">
                <div className="text-12px font-semibold text-white">{`${amberSkillsCount}`}</div>
              </div>
            </div>
            <div className="h-[1px] w-full bg-grey-30" />
          </div>
          <div className="flex flex-col">
            <div className="flex h-14 flex-row items-center justify-between">
              <div>{"Not evidenced"}</div>
              <div className="flex size-7 items-center justify-center rounded-full bg-grey-60">
                <div className="text-12px font-semibold text-white">{`${redSkillsCount}`}</div>
              </div>
            </div>
            <div className="h-[1px] w-full bg-grey-30" />
          </div>
        </>
      )}
    </div>
  );
};

export default SkillsRAGDisplay;
